/**
 * @fileoverview Direct API calls for Redux thunks
 * These are not hooks, just direct API functions
 */

import type { ChatMessageSchema } from "@nexus/types"
import type { EmbedChatRequestHeaders } from "@nexus/types/api-envelope"

import { toFetchHeaders } from "./chat-request-headers"
import { getBackendUrl, isEmbedApp } from "./config"
import { currentAncestorOrigin, EMBED_ANCESTOR_ORIGIN_HEADER } from "./embed-ancestor-origin"
import { readEmbedRequestError } from "./embed-request-error"
import { EMBED_URL_KEY_MAP } from "./embed-url-identity-params"
import { type EmbeddedMessagesPage, readEmbeddedMessagesPage } from "./embedded-messages-page"

// Identity field names that sdata is not allowed to set, even when the
// corresponding identity URL param is absent — prevents a host page from
// impersonating identity via the session-data channel.
const RESERVED_IDENTITY_KEYS = new Set(Object.values(EMBED_URL_KEY_MAP))

/**
 * Extract embed identity data from a params source. When the `cattrs` field is
 * present it must be a JSON object string; malformed values are silently
 * dropped so a bad attribute payload does not corrupt the rest of the identity.
 *
 * Also supports a `sdata` param: a JSON-encoded flat object of arbitrary
 * session-only metadata (e.g. current page, plan tier). Its keys are spread
 * into the result alongside identity fields. Reserved identity keys cannot be
 * set via sdata; nested objects/arrays and null values are dropped; numbers
 * and booleans are coerced to string (the API schema is Record<string,string>).
 */
export function readEmbedIdentityFromParams(
  source: URLSearchParams | Record<string, string | string[] | undefined>
): Record<string, string> {
  const get = (key: string): string | undefined => {
    if (source instanceof URLSearchParams) return source.get(key) ?? undefined
    const v = source[key]
    if (typeof v === "string") return v
    if (Array.isArray(v) && typeof v[0] === "string") return v[0]
    return undefined
  }

  const data: Record<string, string> = {}
  for (const [urlKey, dataKey] of Object.entries(EMBED_URL_KEY_MAP)) {
    const value = get(urlKey)
    if (!value) continue
    if (urlKey === "cattrs") {
      try {
        const parsed = JSON.parse(value) as unknown
        if (parsed && typeof parsed === "object") data[dataKey] = value
      } catch {
        /* drop malformed JSON */
      }
      continue
    }
    data[dataKey] = value
  }

  const sdataRaw = get("sdata")
  if (sdataRaw) {
    try {
      const parsed = JSON.parse(sdataRaw) as unknown
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          if (RESERVED_IDENTITY_KEYS.has(k)) continue
          if (v == null) continue
          if (typeof v === "string") data[k] = v
          else if (typeof v === "number" || typeof v === "boolean") data[k] = String(v)
          // objects/arrays dropped — keep transport flat
        }
      }
    } catch {
      /* drop malformed JSON */
    }
  }

  return data
}

/** Merge explicit embedData with URL-read identity. Explicit wins on conflict. */
function resolveEmbedData(
  explicit: Record<string, string> | undefined
): Record<string, string> | undefined {
  // URL is the source of truth for embed sessions: the widget script writes
  // identity params into the iframe URL at init time, so reading from
  // window.location is synchronous and race-free. Read it when the page is
  // framed (the widget case) OR when isEmbedApp() is set, which lets a direct
  // top-level load of /embed/<id>?sdata=... pick up identity too. Nothing in
  // this repository sets that flag — EnvironmentConfig.isEmbedApp in config.ts
  // owns why — so apps/frontend's playground/admin pages reach this on the
  // framed branch alone and keep ignoring URL identity params.
  const inEmbedContext =
    typeof window !== "undefined" && (window.self !== window.top || isEmbedApp())
  const fromUrl = inEmbedContext
    ? readEmbedIdentityFromParams(new URLSearchParams(window.location.search))
    : {}
  const merged = { ...fromUrl, ...(explicit ?? {}) }
  return Object.keys(merged).length > 0 ? merged : undefined
}

/**
 * Create a new chat room
 * Supports both embedded chats (with sessionId/deploymentId) and playground chats (with authToken)
 *
 * @param params - Chat room creation parameters
 * @param params.content - Initial message content
 * @param params.id - Message ID (UUID)
 * @param params.knowledgeIds - Array of knowledge base document IDs
 * @param params.images - Array of image URLs (signed read URLs of uploaded images; the objects are private)
 * @param params.agentId - Agent/agent ID
 * @param params.chatRoomId - Frontend-generated chat room UUID (REQUIRED)
 * @param params.sessionId - Session ID for embedded chats
 * @param params.deploymentId - Deployment ID for embedded chats
 * @param params.authToken - Auth token for authenticated requests (Playground)
 * @param params.generateTopic - Request AI-generated topic (only if topic is null)
 * @returns Promise resolving to chat room creation response
 * @throws Error if API request fails
 */
export async function createChatRoom({
  content,
  id,
  knowledgeIds,
  images,
  agentId,
  sessionId,
  deploymentId,
  chatRoomId,
  authToken,
  generateTopic,
  embedData
}: {
  content: string
  id: string
  knowledgeIds: string[]
  images?: string[]
  agentId: string
  sessionId?: string
  deploymentId?: string
  chatRoomId: string // Required UUID
  authToken?: string | null
  generateTopic?: boolean
  /** Customer identity data for embed sessions */
  embedData?: Record<string, string>
}): Promise<unknown> {
  const headers: EmbedChatRequestHeaders = {
    "Content-Type": "application/json",
    "x-agent-id": agentId
  }

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`
  }

  if (sessionId) {
    headers["x-session-id"] = sessionId
  }
  if (deploymentId) {
    headers["x-integration-id"] = deploymentId
  }

  // Telemetry only, and only on this call — the first request of a chat, so
  // one send per conversation rather than one per message. The header is
  // OMITTED when there is no reading; `""`, `"null"` and `"unknown"` would each
  // be indistinguishable from a real origin, and an absent header is the honest
  // encoding of "this browser or this host page gave us nothing". Nothing
  // downstream of here may refuse on it: see `embed-ancestor-origin.ts`.
  const ancestorOrigin = currentAncestorOrigin()
  if (ancestorOrigin !== undefined) {
    headers[EMBED_ANCESTOR_ORIGIN_HEADER] = ancestorOrigin
  }

  const resolvedEmbedData = resolveEmbedData(embedData)
  const body: Record<string, unknown> = {
    content,
    id,
    chatRoomId,
    ...(knowledgeIds && knowledgeIds.length > 0 && { knowledgeIds }),
    ...(images && images.length > 0 && { images }),
    ...(generateTopic !== undefined && { generateTopic }),
    ...(resolvedEmbedData && { embedData: resolvedEmbedData })
  }

  const response = await fetch(`${getBackendUrl()}/api/chatrooms`, {
    method: "POST",
    headers: toFetchHeaders(headers),
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    throw await readEmbedRequestError(response, `Failed to create chat: ${response.statusText}`)
  }

  const result = (await response.json()) as { success: boolean; data: unknown }
  return result.data
}

/**
 * Send message to existing chat room
 * Supports both authenticated (Playground) and unauthenticated (Embed) contexts
 *
 * @param params - Message sending parameters
 * @param params.chatRoomId - Existing chat room UUID
 * @param params.content - Message content
 * @param params.id - Message ID (UUID)
 * @param params.knowledgeIds - Array of knowledge base document IDs
 * @param params.images - Array of image URLs (signed read URLs of uploaded images; the objects are private)
 * @param params.agentId - Agent/agent ID
 * @param params.sessionId - Session ID for embedded chats
 * @param params.deploymentId - Deployment ID for embedded chats
 * @param params.authToken - Auth token for authenticated requests (Playground)
 * @param params.generateTopic - Request AI-generated topic (only if topic is null)
 * @returns Promise resolving to message sending response
 * @throws Error if API request fails
 */
export async function sendMessageToChat({
  chatRoomId,
  content,
  id,
  knowledgeIds,
  images,
  agentId,
  sessionId,
  deploymentId,
  authToken,
  generateTopic
}: {
  chatRoomId: string
  content: string
  id: string
  knowledgeIds: string[]
  images?: string[]
  agentId?: string
  sessionId?: string
  deploymentId?: string
  authToken?: string | null
  generateTopic?: boolean
}): Promise<unknown> {
  const headers: EmbedChatRequestHeaders = {
    "Content-Type": "application/json"
  }

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`
  }

  if (agentId) {
    headers["x-agent-id"] = agentId
  }
  if (sessionId) {
    headers["x-session-id"] = sessionId
  }
  if (deploymentId) {
    headers["x-integration-id"] = deploymentId
  }

  const payload = {
    content,
    id,
    ...(knowledgeIds && knowledgeIds.length > 0 && { knowledgeIds }),
    ...(images && images.length > 0 && { images }),
    ...(generateTopic !== undefined && { generateTopic })
  }

  const response = await fetch(`${getBackendUrl()}/api/chatrooms/${chatRoomId}/messages`, {
    method: "POST",
    headers: toFetchHeaders(headers),
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    throw await readEmbedRequestError(response, `Failed to send message: ${response.statusText}`)
  }

  const result = (await response.json()) as { success: boolean; data: unknown }
  return result.data
}

/**
 * Response type for paginated message loading
 */
export interface LoadChatMessagesResponse {
  messages: ChatMessageSchema[]
  hasMore: boolean
  count: number
}

/**
 * Load messages for a chat room with pagination support
 * Supports both authenticated (Playground) and unauthenticated (Embed) contexts
 *
 * @param params.chatRoomId - The chat room ID
 * @param params.authToken - Auth token for authenticated requests
 * @param params.limit - Number of messages to fetch (default: 50)
 * @param params.before - Cursor: fetch messages before this message ID (for pagination)
 * @param params.order - Sort order: "desc" (newest first) or "asc" (oldest first)
 */
export async function loadChatMessages({
  chatRoomId,
  authToken,
  limit = 50,
  before,
  order = "desc"
}: {
  chatRoomId: string
  authToken?: string | null
  limit?: number
  before?: string
  order?: "asc" | "desc"
}): Promise<LoadChatMessagesResponse> {
  const headers: EmbedChatRequestHeaders = {
    "Content-Type": "application/json"
  }

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`
  }

  const params = new URLSearchParams({
    sortBy: "createdAt",
    order,
    limit: String(limit)
  })
  if (before) {
    params.append("before", before)
  }

  const response = await fetch(
    `${getBackendUrl()}/api/chatrooms/${chatRoomId}/messages?${params}`,
    {
      method: "GET",
      headers: toFetchHeaders(headers)
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to load messages: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Load embedded chat messages
 */
export async function getEmbeddedChatMessages({
  chatRoomId,
  agentId,
  sessionId,
  deploymentId,
  limit = "50",
  sortBy = "createdAt",
  order = "asc",
  before
}: {
  chatRoomId: string
  agentId: string
  sessionId: string
  deploymentId: string
  limit?: string
  sortBy?: string
  order?: string
  before?: string
}): Promise<EmbeddedMessagesPage> {
  const params = new URLSearchParams({ limit, sortBy, order })
  if (before) {
    params.append("before", before)
  }

  const response = await fetch(
    `${getBackendUrl()}/api/chatrooms/${chatRoomId}/messages?${params}`,
    {
      method: "GET",
      headers: toFetchHeaders({
        "Content-Type": "application/json",
        "x-agent-id": agentId,
        "x-session-id": sessionId,
        "x-integration-id": deploymentId
      })
    }
  )

  if (!response.ok) {
    // The message stays the fixed one this read always threw — a mount-time failure
    // is shown to the visitor verbatim — but the envelope code now travels with it.
    throw await readEmbedRequestError(
      response,
      `Failed to load embedded chat messages: ${response.statusText}`,
      "fallback"
    )
  }

  const page = readEmbeddedMessagesPage(await response.json())

  if (page === null) {
    throw new Error("Failed to load embedded chat messages: unexpected response shape")
  }

  return page
}

/**
 * Stop message processing for a chat room
 * Cancels any in-progress AI responses and workflow executions
 *
 * @param params - Stop parameters
 * @param params.chatId - Chat room UUID
 * @param params.authToken - Auth token for authenticated requests (Playground)
 * @param params.agentId - Public agent ID for embedded chats; the embed auth guard
 *   authenticates an anonymous widget request by it, so an embed stop without it is a 401
 * @param params.sessionId - Session ID for embedded chats
 * @param params.deploymentId - Deployment ID for embedded chats
 * @returns Promise resolving to stop result with count of cancelled messages
 */
export async function stopMessageProcessing({
  chatId,
  authToken,
  agentId,
  sessionId,
  deploymentId
}: {
  chatId: string
  authToken?: string | null
  agentId?: string
  sessionId?: string
  deploymentId?: string
}): Promise<{ count: number; workflowsStopped: number }> {
  const headers: EmbedChatRequestHeaders = {
    "Content-Type": "application/json"
  }

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`
  }
  if (agentId) {
    headers["x-agent-id"] = agentId
  }
  if (sessionId) {
    headers["x-session-id"] = sessionId
  }
  if (deploymentId) {
    headers["x-integration-id"] = deploymentId
  }

  const response = await fetch(`${getBackendUrl()}/api/chatrooms/${chatId}/stop`, {
    method: "PUT",
    headers: toFetchHeaders(headers)
  })

  if (!response.ok) {
    throw new Error(`Failed to stop message processing: ${response.statusText}`)
  }

  return response.json()
}
